<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRules, type SubmitEventPromise } from 'vuetify'
import { useEmailLogin } from './useEmailLogin'
import { handleAuthCodeCallback } from '@/composables/useAuth.ts'
import useAuthStore from '@/stores/authStore'
import mdiArrowLeft from '~icons/mdi/arrow-left'

const props = withDefaults(
  defineProps<{
    /** The address step 1 looked up; still editable before the code is requested. */
    email: string
    /** The "remember me" choice from step 2, applied to the login the code completes. */
    sessionParams?: { endSessionOnClose?: 'true' }
  }>(),
  { sessionParams: () => ({}) }
)

const emit = defineEmits<{
  back: []
  vismaNet: [email: string]
  error: [value: string | null]
}>()

const { t } = useI18n()
const rules = useRules()
const { sendLoginCode, loading, emailSent, error } = useEmailLogin()
const authStore = useAuthStore()

const otpCodeIncorrect = ref(false)
const emailLoginState = ref('')
const input = ref({
  email: props.email,
  otpCode: '',
})

watch(error, (value) => {
  emit('error', value)
})

async function handleSendEmailCode() {
  if (input.value.email === '') return
  const success = await sendLoginCode(input.value.email)
  if (success) {
    // The address doubles as the PKCE verifier for the one-time-code exchange.
    emailLoginState.value = authStore.addAuthSession(input.value.email, undefined, props.sessionParams)
  }
}

async function handleSubmit(event: SubmitEventPromise) {
  const { valid } = await event
  if (!valid) return
  await handleSendEmailCode()
}

async function handleOtpFinish(code: string) {
  otpCodeIncorrect.value = false
  try {
    const redirectUrl = await handleAuthCodeCallback(code, emailLoginState.value)
    window.location.href = redirectUrl
  } catch (err) {
    // The auth session survives a rejected code, so the user retypes into empty fields and tries again.
    otpCodeIncorrect.value = true
    input.value.otpCode = ''
    console.error('handleAuthCodeCallback error:', err)
  }
}
</script>

<template>
  <div>
    <v-form
      v-if="!emailSent"
      @submit.prevent="handleSubmit"
    >
      <p class="mb-6">{{ t('common.otp_enter_email_description') }}</p>
      <v-text-field
        v-model="input.email"
        :label="t('common.email')"
        :rules="[rules.required(), rules.email()]"
        type="email"
        autocomplete="username"
        class="mb-2"
      />
      <v-btn
        type="submit"
        color="primary"
        :loading="loading"
      >
        {{ t('common.send_otp_code') }}
      </v-btn>
    </v-form>

    <div v-else>
      <!-- The text never says whether a code was sent, so the address itself tells which inbox to check. -->
      <p class="text-body-2 mb-1">{{ input.email }}</p>
      <p class="mb-4">{{ t('common.otp_code_sent_to') }}</p>
      <div class="d-flex align-center">
        <v-otp-input
          v-model="input.otpCode"
          density="compact"
          @finish="handleOtpFinish"
        />
        <v-progress-circular
          v-if="loading"
          class="ml-4"
          color="primary"
          indeterminate
        />
      </div>
      <p
        v-if="otpCodeIncorrect"
        class="text-error"
      >
        {{ t('common.otp_code_not_correct') }}
      </p>
      <p class="mt-2 d-flex align-center flex-wrap ga-1">
        {{ t('common.no_code_received') }}
        <v-btn
          variant="text"
          size="small"
          class="px-1 text-decoration-underline"
          :loading="loading"
          @click="handleSendEmailCode"
        >
          {{ t('common.send_again') }}
        </v-btn>
      </p>
      <!-- Fallback entry for Visma.net tenants without a user document: no code ever arrives for them. -->
      <v-btn
        variant="text"
        size="small"
        class="mt-2 px-0"
        @click="emit('vismaNet', input.email)"
      >
        {{ t('common.visma_net_customer_sign_in') }}
      </v-btn>
    </div>

    <v-btn
      class="mt-6"
      variant="text"
      color="primary"
      @click="emit('back')"
    >
      <v-icon
        :icon="mdiArrowLeft"
        class="mr-2"
      />
      {{ t('common.choose_another_login_method') }}
    </v-btn>
  </div>
</template>
